import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";
import * as XLSX from "xlsx";

// ── Componente principal ────────────────────────────────────
const StockAlmacen = () => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [almacen, setAlmacen] = useState("");
  const [mostrarSinStock, setMostrarSinStock] = useState(false);

  // Obtener almacén del usuario al montar
  useEffect(() => {
    const sapUser = JSON.parse(localStorage.getItem("sapUser"));
    if (sapUser?.Almacen) {
      setAlmacen(sapUser.Almacen);
    }
  }, []);

  // ── Obtener stock ─────────────────────────────────────────
  const fetchStock = async () => {
    if (!almacen) {
      setError("No se pudo determinar el almacén del usuario.");
      return;
    }
    setLoading(true);
    setError("");
    const sapUser = JSON.parse(localStorage.getItem("sapUser"));
    const company = sapUser?.CompanyDB || "";
    try {
      const { data } = await apiClient.get("/inventario/stock", {
        params: { whsCode: almacen, company },
      });

      if (data?.error) {
        setError(data.error);
        setStockData([]);
        return;
      }

      const arr = Array.isArray(data) ? data : [];
      setStockData(arr);
    } catch (e) {
      console.error(e);
      setError("Error al obtener el stock.");
      setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Tipos de artículo únicos (para filtro dinámico) ───────
  const tiposUnicos = useMemo(() => {
    const set = new Set(stockData.map((r) => r.TipoArticulo).filter(Boolean));
    return ["", ...Array.from(set).sort()];
  }, [stockData]);

  // ── Filtro combinado (texto libre + tipo de artículo + sin stock) ─────
  const filteredData = useMemo(() => {
    let rows = stockData;

    // Ocultar artículos sin stock (por defecto)
    if (!mostrarSinStock) {
      rows = rows.filter((r) => Number(r.OnHand) !== 0);
    }

    if (filterTipo) {
      rows = rows.filter((r) => r.TipoArticulo === filterTipo);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.ItemCode || "").toLowerCase().includes(q) ||
          (r.ItemName || "").toLowerCase().includes(q) ||
          (r.TipoArticulo || "").toLowerCase().includes(q) ||
          (r.UoM || "").toLowerCase().includes(q)
      );
    }

    return rows;
  }, [stockData, searchTerm, filterTipo, mostrarSinStock]);

  // ── Exportar a Excel ──────────────────────────────────────
  const exportarExcel = () => {
    if (!filteredData.length) {
      alert("No hay datos para exportar.");
      return;
    }

    const wsData = filteredData.map((r) => ({
      "Código de Artículo": r.ItemCode,
      "Nombre de Artículo": r.ItemName,
      "Unidad de Medida": r.UoM,
      "Tipo Artículo": r.TipoArticulo,
      "Cantidad en Almacén": r.OnHand,
      "Almacén": r.WhsCode,
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);

    // Ajustar anchos de columna
    ws["!cols"] = [
      { wch: 18 }, // Código
      { wch: 45 }, // Nombre
      { wch: 16 }, // UM
      { wch: 18 }, // Tipo
      { wch: 20 }, // Cantidad
      { wch: 12 }, // Almacén
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Stock_${almacen}_${today}.xlsx`);
  };

  // ── Columnas DataTable ────────────────────────────────────
  const columns = [
    {
      name: "Código de Artículo",
      selector: (row) => row.ItemCode,
      sortable: true,
      width: "180px",
    },
    {
      name: "Nombre de Artículo",
      selector: (row) => row.ItemName,
      sortable: true,
      wrap: true,
      minWidth: "320px",
    },
    {
      name: "Unidad de Medida",
      selector: (row) => row.UoM,
      sortable: true,
      width: "150px",
    },
    {
      name: "Tipo Artículo",
      selector: (row) => row.TipoArticulo,
      sortable: true,
      width: "170px",
      cell: (row) => {
        const colors = {
          Perecederos: "bg-warning text-dark",
          "No Perecederos": "bg-secondary",
          Cárnicos: "bg-danger",
          Limpieza: "bg-info text-dark",
          Utensilios: "bg-primary",
          Equipamientos: "bg-dark",
          Líquido: "bg-success",
          "Sin Clasificar": "bg-light text-dark border",
        };
        const cls = colors[row.TipoArticulo] || "bg-light text-dark border";
        return <span className={`badge ${cls}`}>{row.TipoArticulo}</span>;
      },
    },
    {
      name: "Cantidad en Almacén",
      selector: (row) => row.OnHand,
      sortable: true,
      right: true,
      width: "180px",
      cell: (row) => (
        <span style={{ fontWeight: 600 }}>
          {Number(row.OnHand).toLocaleString("es-ES", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })}
        </span>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="container mt-4">
      <h2>Stock por Almacén</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filtros */}
      <div className="row g-2 align-items-end">
        <div className="col-md-2">
          <label className="form-label">Almacén:</label>
          <input
            type="text"
            className="form-control"
            value={almacen}
            onChange={(e) => setAlmacen(e.target.value)}
            placeholder="Ej: ITA"
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Tipo Artículo:</label>
          <select
            className="form-select"
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
          >
            <option value="">— Todos —</option>
            {tiposUnicos
              .filter((t) => t !== "")
              .map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">Buscar:</label>
          <input
            type="text"
            className="form-control"
            placeholder="Código, nombre, tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="col-md-auto d-flex align-items-end">
          <div className="form-check form-switch mb-1">
            <input
              className="form-check-input"
              type="checkbox"
              id="toggleSinStock"
              checked={mostrarSinStock}
              onChange={(e) => setMostrarSinStock(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="toggleSinStock" style={{ fontSize: 13 }}>
              Mostrar sin stock
            </label>
          </div>
        </div>

        <div className="col-md-auto">
          <button
            className="btn btn-success"
            onClick={fetchStock}
            disabled={loading}
          >
            {loading ? "Cargando..." : "Consultar Stock"}
          </button>
        </div>

        <div className="col-md-auto">
          <button
            className="btn btn-outline-primary"
            onClick={exportarExcel}
            disabled={loading || !filteredData.length}
          >
            📥 Exportar a Excel
          </button>
        </div>
      </div>

      {/* Resumen */}
      {stockData.length > 0 && (
        <div className="mt-2 text-muted" style={{ fontSize: 13 }}>
          Mostrando <strong>{filteredData.length}</strong> de{" "}
          <strong>{stockData.length}</strong> artículos | Almacén:{" "}
          <strong>{almacen}</strong>
        </div>
      )}

      {/* Tabla */}
      <DataTable
        className="mt-2"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25, 50, 100, 200]}
        highlightOnHover
        striped
        dense
        noDataComponent="No hay datos. Presione 'Consultar Stock' para obtener el stock del almacén."
      />
    </div>
  );
};

export default StockAlmacen;
